import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useBankAccounts, useDeleteBankAccount } from 'hooks/api/useBankAccounts';
import type { BankAccount, BankAccountSearchRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import BankAccountModal from 'components/modals/modules/bank-account-module/BankAccountModal';
import BankAccountViewModal from 'components/modals/modules/bank-account-module/BankAccountViewModal';

type ViewMode = 'list' | 'grid';

const statusOptions: SelectOption[] = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

interface BankAccountMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const BankAccountMasterScreen: React.FC<BankAccountMasterScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<BankAccountSearchRequest>({ page: 1, pageSize: 25 });
  const { data, pagination, refetch } = useBankAccounts(queryParams);
  const { deleteBankAccount, loading: isDeleting } = useDeleteBankAccount();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<BankAccount | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleView = useCallback((item: BankAccount) => {
    const viewId = tabId ? `${tabId}-view-modal-${item.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(item);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((id: string) => {
    const item = data.find(o => o.id === id);
    if (item) { setSelectedRecord(item); setViewModalOpen(true); }
  }, [data]);

  const handleEdit = useCallback((item: BankAccount) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${item.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(item);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((item: BankAccount) => {
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setSelectedRecord(item);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((item: BankAccount) => {
    setSelectedRecord(item);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await deleteBankAccount(selectedRecord.id, selectedRecord.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedRecord.accountCode}" deleted successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while deleting. Please try again.');
    }
  }, [selectedRecord, deleteBankAccount, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, recordId?: string) => {
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (recordId) {
      const item = data.find(o => o.id === recordId);
      if (item) setSelectedRecord(item);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [data]);

  const tableColumns: SyutGridColumn<BankAccount>[] = useMemo(() => [
    { id: 'accountCode', header: 'Code', accessorKey: 'accountCode', size: 140, enableSorting: true },
    { id: 'accountName', header: 'Account Name', accessorKey: 'accountName', size: 220, enableSorting: true },
    { id: 'bankName', header: 'Bank', accessorKey: 'bankName', size: 160, enableSorting: true },
    {
      id: 'accountNumber', header: 'Account No.', accessorKey: 'accountNumber', size: 160,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'ifscCode', header: 'IFSC', accessorKey: 'ifscCode', size: 120,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'mappedProperties', header: 'Properties', accessorKey: 'mappedProperties', size: 240,
      cell: ({ getValue }) => {
        const val = (getValue() as string) || '-';
        const isShared = val === '(All / Shared)';
        return (
          <span style={{ fontSize: '0.85rem', color: isShared ? 'var(--text-secondary)' : 'var(--text-primary)', fontStyle: isShared ? 'italic' : undefined }}>
            {val}
          </span>
        );
      },
    },
    {
      id: 'isActive', header: 'Status', accessorKey: 'isActive', size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>{isActive ? 'Active' : 'Inactive'}</span>;
      },
    },
  ], []);

  const getRowActions = useCallback((item: BankAccount): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(item) });
    }
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(item) });
    }
    if (allowedActions.includes('add')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(item) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(item), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleView, handleEdit, handleCopy, handleDeleteClick]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (allowedActions.includes('add')) {
      buttons.push({ id: 'add', icon: <IconSystem.add size={18} />, label: 'Add Account', onClick: handleAddNew, tooltip: 'Add New Bank Account', variant: 'icon' });
    }
    buttons.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' });
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      includeInactive: filterStatus === 'true' ? false : true,
      page: 1,
    }));
  }, [filterStatus]);

  const handleFilterClear = useCallback(() => {
    setFilterStatus('');
    setQueryParams(prev => ({ ...prev, includeInactive: undefined, page: 1 }));
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Bank Account Master"
        description="Manage receiving bank accounts for booking payments"
        icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : <IconSystem.creditCard size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search bank accounts...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as ViewMode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount: (filterStatus ? 1 : 0) }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={data}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No bank accounts found"
              frozenLeftColumns={['accountCode']}
              frozenRightColumns={['actions']}
            />
          )}
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }} title="Close filters">
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={statusOptions}
                  value={filterStatus}
                  onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleFilterClear}>Clear</button>
              <button className="btn btn-primary" onClick={handleFilterApply}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <BankAccountViewModal
        isOpen={viewModalOpen}
        bankAccount={selectedRecord}
        onClose={() => setViewModalOpen(false)}
        onEdit={(item) => handleEdit(item)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      <BankAccountModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedRecord?.accountCode}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />
    </div>
  );
};

export default BankAccountMasterScreen;
